/**
 * API Request Optimization Utilities
 * Implements request deduplication, batching, and pagination optimization
 * Requirements: 8.3, 8.5
 */

/**
 * Request deduplication manager
 * Prevents duplicate API calls for the same resource
 */
class RequestDeduplicationManager {
  constructor() {
    this.pendingRequests = new Map();
    this.requestCache = new Map();
    this.cacheTimeout = 5000; // 5 seconds cache for deduplication
  }

  /**
   * Execute request with deduplication
   * @param {string} key - Unique key for the request
   * @param {Function} requestFn - Function that makes the request
   * @param {number} cacheTime - Cache time in milliseconds
   * @returns {Promise} Request result
   */
  async executeRequest(key, requestFn, cacheTime = this.cacheTimeout) {
    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      if (process.env.NODE_ENV === "development") {
        console.log(`🔄 Request deduplicated: ${key}`);
      }
      return this.pendingRequests.get(key);
    }

    // Check cache for recent results
    const cached = this.requestCache.get(key);
    if (cached && Date.now() - cached.timestamp < cacheTime) {
      if (process.env.NODE_ENV === "development") {
        console.log(`⚡ Request cache hit: ${key}`);
      }
      return cached.data;
    }

    // Create new request
    const requestPromise = requestFn()
      .then((result) => {
        // Cache the result
        this.requestCache.set(key, {
          data: result,
          timestamp: Date.now(),
        });

        // Clean up pending request
        this.pendingRequests.delete(key);

        return result;
      })
      .catch((error) => {
        // Clean up pending request on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store pending request
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  /**
   * Clear cache for a specific key
   * @param {string} key - Cache key to clear
   */
  clearCache(key) {
    this.requestCache.delete(key);
    this.pendingRequests.delete(key);
  }

  /**
   * Clear all caches
   */
  clearAllCaches() {
    this.requestCache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      cachedResults: this.requestCache.size,
      cacheKeys: Array.from(this.requestCache.keys()),
    };
  }
}

/**
 * Request batching manager
 * Batches multiple similar requests into single API calls
 */
class RequestBatchingManager {
  constructor(options = {}) {
    this.batchTimeout = options.batchTimeout || 50; // 50ms batch window
    this.maxBatchSize = options.maxBatchSize || 10;
    this.pendingBatches = new Map();
  }

  /**
   * Add request to batch
   * @param {string} batchKey - Key identifying the batch type
   * @param {string} itemId - Individual item ID
   * @param {Function} batchRequestFn - Function to execute batch request
   * @returns {Promise} Promise for the individual item result
   */
  batchRequest(batchKey, itemId, batchRequestFn) {
    return new Promise((resolve, reject) => {
      // Get or create batch
      let batch = this.pendingBatches.get(batchKey);
      if (!batch) {
        batch = {
          items: new Map(),
          timeout: null,
          batchRequestFn,
        };
        this.pendingBatches.set(batchKey, batch);
      }

      // Add item to batch
      batch.items.set(itemId, { resolve, reject });

      // Clear existing timeout
      if (batch.timeout) {
        clearTimeout(batch.timeout);
      }

      // Execute batch if it's full or set timeout
      if (batch.items.size >= this.maxBatchSize) {
        this.executeBatch(batchKey);
      } else {
        batch.timeout = setTimeout(() => {
          this.executeBatch(batchKey);
        }, this.batchTimeout);
      }
    });
  }

  /**
   * Execute a batch request
   * @param {string} batchKey - Batch key to execute
   */
  async executeBatch(batchKey) {
    const batch = this.pendingBatches.get(batchKey);
    if (!batch || batch.items.size === 0) return;

    // Remove batch from pending
    this.pendingBatches.delete(batchKey);

    // Clear timeout
    if (batch.timeout) {
      clearTimeout(batch.timeout);
    }

    const itemIds = Array.from(batch.items.keys());

    if (process.env.NODE_ENV === "development") {
      console.log(
        `📦 Executing batch request: ${batchKey} (${itemIds.length} items)`
      );
    }

    try {
      // Execute batch request
      const results = await batch.batchRequestFn(itemIds);

      // Resolve individual promises
      itemIds.forEach((itemId) => {
        const item = batch.items.get(itemId);
        if (item && results[itemId]) {
          item.resolve(results[itemId]);
        } else if (item) {
          item.reject(new Error(`No result for item: ${itemId}`));
        }
      });
    } catch (error) {
      // Reject all promises in batch
      batch.items.forEach((item) => {
        item.reject(error);
      });
    }
  }

  /**
   * Clear all pending batches
   */
  clearBatches() {
    this.pendingBatches.forEach((batch) => {
      if (batch.timeout) {
        clearTimeout(batch.timeout);
      }
      batch.items.forEach((item) => {
        item.reject(new Error("Batch cleared"));
      });
    });
    this.pendingBatches.clear();
  }
}

/**
 * Pagination optimization manager
 * Optimizes pagination parameters and prefetching
 */
class PaginationOptimizer {
  constructor(options = {}) {
    this.defaultPageSize = options.defaultPageSize || 20;
    this.maxPageSize = options.maxPageSize || 100;
    this.prefetchThreshold = options.prefetchThreshold || 0.8; // Prefetch when 80% scrolled
    this.adaptivePageSize = options.adaptivePageSize || true;
  }

  /**
   * Calculate optimal page size based on viewport and performance
   * @param {number} viewportHeight - Viewport height in pixels
   * @param {number} itemHeight - Average item height in pixels
   * @param {number} networkSpeed - Network speed indicator (1-5)
   * @returns {number} Optimal page size
   */
  calculateOptimalPageSize(viewportHeight, itemHeight = 100, networkSpeed = 3) {
    if (!this.adaptivePageSize) {
      return this.defaultPageSize;
    }

    // Calculate items that fit in viewport
    const itemsInViewport = Math.ceil(viewportHeight / itemHeight);

    // Base page size on viewport with buffer
    let optimalSize = Math.max(itemsInViewport * 2, this.defaultPageSize);

    // Adjust based on network speed
    const networkMultiplier = {
      1: 0.5, // Slow network - smaller pages
      2: 0.75,
      3: 1.0, // Average network
      4: 1.25,
      5: 1.5, // Fast network - larger pages
    };

    optimalSize = Math.round(
      optimalSize * (networkMultiplier[networkSpeed] || 1)
    );

    // Ensure within bounds
    return Math.min(Math.max(optimalSize, 5), this.maxPageSize);
  }

  /**
   * Determine if prefetching should occur
   * @param {number} scrollPosition - Current scroll position (0-1)
   * @param {boolean} hasMore - Whether more data is available
   * @param {boolean} isLoading - Whether currently loading
   * @returns {boolean} Whether to prefetch
   */
  shouldPrefetch(scrollPosition, hasMore, isLoading) {
    return scrollPosition >= this.prefetchThreshold && hasMore && !isLoading;
  }

  /**
   * Calculate prefetch pages
   * @param {number} currentPage - Current page number
   * @param {number} totalPages - Total pages available
   * @param {number} prefetchCount - Number of pages to prefetch
   * @returns {Array} Array of page numbers to prefetch
   */
  calculatePrefetchPages(currentPage, totalPages, prefetchCount = 2) {
    const pages = [];

    for (let i = 1; i <= prefetchCount; i++) {
      const nextPage = currentPage + i;
      if (nextPage <= totalPages) {
        pages.push(nextPage);
      }
    }

    return pages;
  }
}

/**
 * Network performance monitor
 * Monitors network performance to optimize requests
 */
class NetworkPerformanceMonitor {
  constructor() {
    this.requestTimes = [];
    this.maxSamples = 20;
    this.networkSpeed = 3; // Default to average
  }

  /**
   * Record request timing
   * @param {number} duration - Request duration in milliseconds
   * @param {number} dataSize - Data size in bytes (optional)
   */
  recordRequest(duration, dataSize = 0) {
    this.requestTimes.push({
      duration,
      dataSize,
      timestamp: Date.now(),
    });

    // Keep only recent samples
    if (this.requestTimes.length > this.maxSamples) {
      this.requestTimes.shift();
    }

    // Update network speed estimate
    this.updateNetworkSpeed();
  }

  /**
   * Update network speed estimate
   */
  updateNetworkSpeed() {
    if (this.requestTimes.length < 3) return;

    const recentRequests = this.requestTimes.slice(-10);
    const avgDuration =
      recentRequests.reduce((sum, req) => sum + req.duration, 0) /
      recentRequests.length;

    // Classify network speed (1-5 scale)
    if (avgDuration < 200) {
      this.networkSpeed = 5; // Very fast
    } else if (avgDuration < 500) {
      this.networkSpeed = 4; // Fast
    } else if (avgDuration < 1000) {
      this.networkSpeed = 3; // Average
    } else if (avgDuration < 2000) {
      this.networkSpeed = 2; // Slow
    } else {
      this.networkSpeed = 1; // Very slow
    }
  }

  /**
   * Get current network speed estimate
   * @returns {number} Network speed (1-5)
   */
  getNetworkSpeed() {
    return this.networkSpeed;
  }

  /**
   * Get performance statistics
   * @returns {Object} Performance statistics
   */
  getStats() {
    if (this.requestTimes.length === 0) {
      return {
        avgDuration: 0,
        networkSpeed: this.networkSpeed,
        sampleCount: 0,
      };
    }

    const avgDuration =
      this.requestTimes.reduce((sum, req) => sum + req.duration, 0) /
      this.requestTimes.length;
    const recentAvg =
      this.requestTimes.slice(-5).reduce((sum, req) => sum + req.duration, 0) /
      Math.min(5, this.requestTimes.length);

    return {
      avgDuration: Math.round(avgDuration),
      recentAvgDuration: Math.round(recentAvg),
      networkSpeed: this.networkSpeed,
      sampleCount: this.requestTimes.length,
      trend:
        recentAvg < avgDuration
          ? "improving"
          : recentAvg > avgDuration
          ? "degrading"
          : "stable",
    };
  }
}

// Create singleton instances
export const requestDeduplication = new RequestDeduplicationManager();
export const requestBatching = new RequestBatchingManager();
export const paginationOptimizer = new PaginationOptimizer();
export const networkMonitor = new NetworkPerformanceMonitor();

/**
 * High-level API optimization wrapper
 */
export class APIOptimizer {
  constructor() {
    this.deduplication = requestDeduplication;
    this.batching = requestBatching;
    this.pagination = paginationOptimizer;
    this.network = networkMonitor;
  }

  /**
   * Optimize a single API request
   * @param {string} key - Request key for deduplication
   * @param {Function} requestFn - Request function
   * @param {Object} options - Optimization options
   * @returns {Promise} Optimized request result
   */
  async optimizeRequest(key, requestFn, options = {}) {
    const startTime = performance.now();

    try {
      const result = await this.deduplication.executeRequest(
        key,
        requestFn,
        options.cacheTime
      );

      // Record performance
      const duration = performance.now() - startTime;
      this.network.recordRequest(duration, options.dataSize);

      return result;
    } catch (error) {
      // Record failed request
      const duration = performance.now() - startTime;
      this.network.recordRequest(duration, 0);
      throw error;
    }
  }

  /**
   * Optimize pagination request
   * @param {Object} paginationParams - Pagination parameters
   * @param {Function} requestFn - Request function
   * @returns {Promise} Optimized pagination result
   */
  async optimizePaginationRequest(paginationParams, requestFn) {
    const { page, limit, viewportHeight, itemHeight } = paginationParams;

    // Calculate optimal page size
    const networkSpeed = this.network.getNetworkSpeed();
    const optimalLimit = this.pagination.calculateOptimalPageSize(
      viewportHeight || 600,
      itemHeight || 100,
      networkSpeed
    );

    // Use optimal limit if not specified or if it's better
    const finalLimit = limit || optimalLimit;

    const key = `pagination_${page}_${finalLimit}`;
    return this.optimizeRequest(key, () => requestFn(page, finalLimit));
  }

  /**
   * Get optimization statistics
   * @returns {Object} Combined statistics
   */
  getStats() {
    return {
      deduplication: this.deduplication.getStats(),
      network: this.network.getStats(),
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Clear all optimization caches
   */
  clearCaches() {
    this.deduplication.clearAllCaches();
    this.batching.clearBatches();
  }
}

// Export singleton instance
export const apiOptimizer = new APIOptimizer();

// Default export object
const apiOptimizationUtils = {
  requestDeduplication,
  requestBatching,
  paginationOptimizer,
  networkMonitor,
  apiOptimizer,
  APIOptimizer,
};

export default apiOptimizationUtils;
